import React, { useState, useMemo, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import FiltersBar from './FiltersBar';
import SimulationsGrid from './SimulationsGrid';
import NewSimulationDialog from './NewSimulationDialog';
import { mockSimulations } from '../../../../data/mockSimulations';
import { OmbudsmanSimulation } from '../../../../types/workspace';
import { usePageHeader } from '../../../../contexts/PageHeaderContext';

const OmbudsmanWorkspaceHub: React.FC = () => {
  const navigate = useNavigate();
  const { setPageInfo } = usePageHeader();

  // Hide the V3Header for this page - use useLayoutEffect to run before paint
  useLayoutEffect(() => {
    setPageInfo({ title: '', description: '', hideHeader: true });
    return () => {
      setPageInfo(null);
    };
  }, [setPageInfo]);

  // Dialog state
  const [isNewSimulationDialogOpen, setIsNewSimulationDialogOpen] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [dateRangeFilter, setDateRangeFilter] = useState('all');

  // Filter simulations based on current filters
  const filteredSimulations = useMemo(() => {
    let filtered = [...mockSimulations];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((sim) =>
        sim.title.toLowerCase().includes(query)
      );
    }

    // Product type filter
    if (productTypeFilter !== 'all') {
      filtered = filtered.filter((sim) => sim.productType === productTypeFilter);
    }

    // Outcome filter
    if (outcomeFilter !== 'all') {
      filtered = filtered.filter((sim) => sim.predicted_outcome === outcomeFilter);
    }

    // Date range filter (basic implementation for demo)
    if (dateRangeFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      filtered = filtered.filter((sim) => {
        const updatedDate = new Date(sim.updated_at);

        switch (dateRangeFilter) {
          case 'today':
            return updatedDate >= today;
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return updatedDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return updatedDate >= monthAgo;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [searchQuery, productTypeFilter, outcomeFilter, dateRangeFilter]);

  // Check if any filters are active
  const isFiltered = useMemo(() => {
    return (
      searchQuery.trim() !== '' ||
      productTypeFilter !== 'all' ||
      outcomeFilter !== 'all' ||
      dateRangeFilter !== 'all'
    );
  }, [searchQuery, productTypeFilter, outcomeFilter, dateRangeFilter]);

  // Handlers
  const handleStartNew = () => {
    setIsNewSimulationDialogOpen(true);
  };

  const handleOpen = (id: string) => {
    navigate(`/v3/workspace/ombudsman/${id}`);
  };

  const handleExportPDF = (id: string) => {
    // TODO: Export PDF
    console.log('Export PDF for simulation:', id);
    alert(`Export PDF for ${id} - Coming soon!`);
  };

  const handleDownloadJSON = (id: string) => {
    // TODO: Download JSON
    const simulation = mockSimulations.find((s) => s.id === id);
    if (simulation) {
      const dataStr = JSON.stringify(simulation, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `simulation-${id}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleArchive = (id: string) => {
    // TODO: Archive simulation
    console.log('Archive simulation:', id);
    alert(`Archive ${id} - Coming soon!`);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setProductTypeFilter('all');
    setOutcomeFilter('all');
    setDateRangeFilter('all');
  };

  return (
    <div className="flex flex-col h-full pb-24">
      {/* Page Header */}
      <PageHeader />

      {/* Filters Bar */}
      <FiltersBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        productTypeFilter={productTypeFilter}
        onProductTypeChange={setProductTypeFilter}
        outcomeFilter={outcomeFilter}
        onOutcomeChange={setOutcomeFilter}
        dateRangeFilter={dateRangeFilter}
        onDateRangeChange={setDateRangeFilter}
        filteredCount={filteredSimulations.length}
        onStartNew={handleStartNew}
      />

      {/* Main Content: Simulations Grid with padding for Juno orb */}
      <div className="flex-1 overflow-y-auto pb-24">
        <SimulationsGrid
          simulations={filteredSimulations}
          onOpen={handleOpen}
          onExportPDF={handleExportPDF}
          onDownloadJSON={handleDownloadJSON}
          onArchive={handleArchive}
          onStartNew={handleStartNew}
          onClearFilters={handleClearFilters}
          isFiltered={isFiltered}
        />
      </div>

      {/* New Simulation Dialog */}
      <NewSimulationDialog
        open={isNewSimulationDialogOpen}
        onOpenChange={setIsNewSimulationDialogOpen}
      />
    </div>
  );
};

export default OmbudsmanWorkspaceHub;
